import { useState } from "react";
import { toast, Toaster } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Camera, Loader2, X, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SYMPTOMS = ["Leaking", "Drifting", "Bent Rod", "Scored Barrel", "Won't Hold Pressure", "Other"];

export default function Intake() {
  const nav = useNavigate();
  const [f, setF] = useState({
    customer_name: "", phone: "", email: "", company: "",
    equipment_make: "", equipment_model: "",
    specs: { bore: "", rod_diameter: "", stroke: "", pin_to_pin: "", port_size: "", seal_kit_number: "", oem_part_number: "" },
    symptoms: [], other_symptom: "", preferred_contact: "text",
    photo_paths: [], notes: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const updS = (k, v) => setF(p => ({ ...p, specs: { ...p.specs, [k]: v } }));
  const toggleSym = (s) => setF(p => ({ ...p, symptoms: p.symptoms.includes(s) ? p.symptoms.filter(x => x !== s) : [...p.symptoms, s] }));

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const paths = [];
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setF(p => ({ ...p, photo_paths: [...p.photo_paths, ...paths] }));
    setUploading(false);
  };

  const removePhoto = (path) => setF(p => ({ ...p, photo_paths: p.photo_paths.filter(x => x !== path) }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.photo_paths.length === 0) {
      toast.error("At least one photo is required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/intake", f);
      setSuccess(r.data.job_id);
      toast.success("Submitted! Dylan will reach out.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Layout>
        <Toaster theme="dark" />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <h1 className="hero-title text-4xl text-white mt-6">Got it — thank you.</h1>
          <p className="text-slate-300 mt-4">Reference: <span className="font-mono-tech text-red-500">{success.slice(0, 8)}</span></p>
          <p className="text-slate-400 mt-2">You'll get a confirmation email. Dylan will follow up with a quote and lead time.</p>
          <button onClick={() => nav("/")} data-testid="back-home" className="mt-8 btn-outline px-6 py-4 rounded-sm">Back Home</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster theme="dark" />
      <section className="mx-auto max-w-3xl px-4 sm:px-8 py-12">
        <div className="eyebrow mb-2">Rebuild Intake</div>
        <h1 className="hero-title text-4xl sm:text-5xl text-white">Tell us about the cylinder.</h1>
        <p className="text-slate-400 mt-3">Fill it out from your phone. Snap photos right from your camera. All specs required so we can quote you accurately.</p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          <Fieldset title="Your Info">
            <Grid>
              <Field label="Name *"><input required data-testid="intake-name" value={f.customer_name} onChange={e => upd("customer_name", e.target.value)} className="input" /></Field>
              <Field label="Phone *"><input required data-testid="intake-phone" type="tel" value={f.phone} onChange={e => upd("phone", e.target.value)} className="input" /></Field>
              <Field label="Email *"><input required data-testid="intake-email" type="email" value={f.email} onChange={e => upd("email", e.target.value)} className="input" /></Field>
              <Field label="Company (optional)"><input data-testid="intake-company" value={f.company} onChange={e => upd("company", e.target.value)} className="input" /></Field>
            </Grid>
            <div className="mt-4">
              <div className="eyebrow mb-2">Preferred Contact</div>
              <div className="flex gap-2 flex-wrap">
                {["text", "call", "email"].map(m => (
                  <button type="button" key={m} data-testid={`intake-pref-${m}`} onClick={() => upd("preferred_contact", m)}
                    className={`px-4 py-2 rounded-sm font-display font-bold uppercase text-sm ${f.preferred_contact === m ? "bg-red-600 text-white" : "btn-outline"}`}>{m}</button>
                ))}
              </div>
            </div>
          </Fieldset>

          <Fieldset title="Equipment">
            <Grid>
              <Field label="Make *"><input required data-testid="intake-make" value={f.equipment_make} onChange={e => upd("equipment_make", e.target.value)} placeholder="John Deere, Kubota, Cat…" className="input" /></Field>
              <Field label="Model *"><input required data-testid="intake-model" value={f.equipment_model} onChange={e => upd("equipment_model", e.target.value)} placeholder="6410, 5075E…" className="input" /></Field>
            </Grid>
          </Fieldset>

          <Fieldset title="Cylinder Specs" subtitle="Fill in what you know — the more, the faster we can quote.">
            <Grid>
              <Field label="Bore"><input data-testid="intake-bore" value={f.specs.bore} onChange={e => updS("bore", e.target.value)} placeholder='3"' className="input" /></Field>
              <Field label="Rod Diameter"><input data-testid="intake-rod" value={f.specs.rod_diameter} onChange={e => updS("rod_diameter", e.target.value)} placeholder='1.5"' className="input" /></Field>
              <Field label="Stroke"><input data-testid="intake-stroke" value={f.specs.stroke} onChange={e => updS("stroke", e.target.value)} placeholder='24"' className="input" /></Field>
              <Field label="Pin to Pin (Retracted)"><input data-testid="intake-pin" value={f.specs.pin_to_pin} onChange={e => updS("pin_to_pin", e.target.value)} placeholder='32"' className="input" /></Field>
              <Field label="Port Size"><input data-testid="intake-port" value={f.specs.port_size} onChange={e => updS("port_size", e.target.value)} placeholder="#8 SAE, 1/2 NPT" className="input" /></Field>
              <Field label="Seal Kit # (optional)"><input data-testid="intake-sealkit" value={f.specs.seal_kit_number} onChange={e => updS("seal_kit_number", e.target.value)} className="input" /></Field>
              <Field label="OEM Part # (optional)"><input data-testid="intake-oem" value={f.specs.oem_part_number} onChange={e => updS("oem_part_number", e.target.value)} className="input" /></Field>
            </Grid>
          </Fieldset>

          <Fieldset title="Symptoms">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SYMPTOMS.map(s => (
                <button key={s} type="button" onClick={() => toggleSym(s)} data-testid={`intake-symptom-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-3 rounded-sm text-sm font-display font-bold uppercase tracking-wider ${f.symptoms.includes(s) ? "bg-red-600 text-white" : "btn-outline"}`}>{s}</button>
              ))}
            </div>
            {f.symptoms.includes("Other") && (
              <textarea data-testid="intake-other-symptom" value={f.other_symptom} onChange={e => upd("other_symptom", e.target.value)} placeholder="Describe the issue…" className="input mt-3 min-h-[80px]" />
            )}
          </Fieldset>

          <Fieldset title="Photos" subtitle="At least one required. Multiple welcome.">
            <label htmlFor="photo-input" data-testid="intake-photo-picker" className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-[#2A2F3A] hover:border-red-500 rounded-sm p-8 transition">
              {uploading ? <Loader2 className="w-8 h-8 text-red-500 animate-spin" /> : <Camera className="w-10 h-10 text-red-500" />}
              <div className="mt-3 font-display font-bold uppercase tracking-wider text-slate-200">{uploading ? "Uploading…" : "Tap to add photos"}</div>
              <div className="text-slate-500 text-xs mt-1">Camera or gallery. JPG / PNG / HEIC.</div>
            </label>
            <input id="photo-input" type="file" accept="image/*" capture="environment" multiple hidden onChange={e => onFiles(e.target.files)} />
            {f.photo_paths.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                {f.photo_paths.map(p => <PhotoThumb key={p} path={p} onRemove={() => removePhoto(p)} />)}
              </div>
            )}
          </Fieldset>

          <Fieldset title="Notes (optional)">
            <textarea data-testid="intake-notes" value={f.notes} onChange={e => upd("notes", e.target.value)} className="input min-h-[100px]" placeholder="Anything else that helps us…" />
          </Fieldset>

          <button type="submit" disabled={submitting || uploading} data-testid="intake-submit"
            className="btn-red px-8 py-5 rounded-sm w-full sm:w-auto inline-flex items-center justify-center gap-2 text-lg disabled:opacity-50">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            Submit Rebuild Request
          </button>
        </form>
      </section>
    </Layout>
  );
}

function Fieldset({ title, subtitle, children }) {
  return (
    <div className="shop-card p-5 sm:p-6">
      <div className="mb-4">
        <div className="font-display font-black uppercase text-2xl text-white">{title}</div>
        {subtitle && <div className="text-slate-400 text-sm mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="eyebrow mb-1">{label}</div>
      {children}
    </label>
  );
}

function PhotoThumb({ path, onRemove }) {
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
  return (
    <div className="relative aspect-square bg-black rounded-sm overflow-hidden border border-[#2A2F3A]">
      <img src={src} alt="cylinder" className="w-full h-full object-cover" />
      <button type="button" onClick={onRemove} className="absolute top-1 right-1 bg-black/80 rounded-sm p-1"><X className="w-3 h-3 text-white" /></button>
    </div>
  );
}
